/**
 * components/landing/home/interactive/views.jsx — the 12 module views that
 * render inside the Phase 2.1 Interactive Product Experience viewer.
 *
 * Each export is a stand-alone React component sized to fit the ~900px
 * viewer frame without inner scroll on a 1440x900 desktop.  Views share
 * the MockDashboardChrome frame (window traffic-lights + fake URL bar)
 * so the entire tour reads as one product family.  Nothing here talks
 * to a live API — all numbers are seeded fixtures so SSR + hydration
 * match perfectly.
 *
 * Registry order MUST match InteractiveProduct.jsx's MODULES const so
 * the left-nav ordering stays in sync.
 */

import clsx from 'clsx'
import {
  Sparkles, BookOpen, Send, ChevronRight,
  TrendingUp, TrendingDown, Activity,
  Building2, Palette, Globe, Settings2, Users, Bell,
  CheckCircle2, PlayCircle, GraduationCap, Award,
  BarChart3, LineChart, PieChart, Layers,
  ClipboardList, Calculator, Wallet, ScanLine, Wand2, Bot,
  CreditCard, Receipt, Zap, ShieldCheck, MessageSquare,
} from 'lucide-react'
import { MiniSparkline, seededWalk } from '../_shared'

// ─── Shared inner-frame helpers ─────────────────────────────────────────
const ViewFrame = ({ title, subtitle, icon: Icon, tone = 'orange', badge, children }) => (
  <div className="p-5 md:p-6 flex flex-col gap-4 h-full">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {Icon && (
          <div className={clsx(
            'flex h-10 w-10 flex-none items-center justify-center rounded-xl border',
            tone === 'orange' ? 'border-brand-gold/30 bg-brand-gold/10 text-brand-gold'
                              : 'border-brand-emerald/30  bg-brand-emerald/10  text-brand-emerald',
          )}><Icon className="h-5 w-5" /></div>
        )}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Module</div>
          <div className="text-white font-semibold text-lg truncate">{title}</div>
          {subtitle && <div className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-none">
        {badge}
        <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-brand-emerald">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald animate-pulse" />Live
        </div>
      </div>
    </div>
    <div className="flex-1 min-h-0">{children}</div>
  </div>
)

const Cell = ({ children, className = '' }) => (
  <div className={clsx('rounded-xl border border-white/5 bg-white/[0.02] p-3', className)}>{children}</div>
)

const Kpi = ({ label, value, delta, tone = 'green', icon: Icon }) => (
  <Cell>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="text-lg md:text-xl font-semibold text-white tabular-nums mt-1">{value}</div>
      </div>
      {Icon && <div className="h-6 w-6 rounded-md bg-white/[0.03] text-zinc-500 flex items-center justify-center"><Icon className="h-3.5 w-3.5" /></div>}
    </div>
    {delta && (
      <div className={clsx('mt-1 inline-flex items-center gap-0.5 text-[10px] font-mono',
        tone === 'green' ? 'text-brand-emerald' : tone === 'red' ? 'text-red-400' : 'text-zinc-400')}>
        {tone === 'green' ? <TrendingUp className="h-3 w-3" /> : tone === 'red' ? <TrendingDown className="h-3 w-3" /> : null}
        {delta}
      </div>
    )}
  </Cell>
)

// ─── 1. Dashboard ──────────────────────────────────────────────────────────
export const DashboardView = () => (
  <ViewFrame
    title="Platform Dashboard"
    subtitle="Manage your entire AI trading business from one intelligent dashboard."
    icon={Activity}
  >
    <div className="grid grid-cols-4 gap-2">
      <Kpi label="Active workspaces" value="12"        delta="+2 this month"       icon={Building2} />
      <Kpi label="Active users"      value="1,842"     delta="+14.2%"              icon={Users} />
      <Kpi label="MRR"               value="$120,000" delta="+8.4%"               icon={CreditCard} />
      <Kpi label="AI activity"       value="1.4M tok"  delta="trending" tone="orange" icon={Sparkles} />
    </div>
    <div className="mt-3 grid grid-cols-12 gap-2">
      <Cell className="col-span-8">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Platform activity · 30d</div>
          <div className="text-[10px] text-brand-emerald font-mono">+38.4%</div>
        </div>
        <MiniSparkline data={seededWalk(101, 120, 5, 100)} stroke="#FFC107" height={160} width={720} className="w-full" />
      </Cell>
      <Cell className="col-span-4 space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Recent activity</div>
        {[['12:04','New tenant · Acme Trading','green'],
          ['11:52','Strategy v3 deployed','orange'],
          ['11:41','Auto-doc approved','green'],
          ['11:22','Backtest • 142 trades','orange'],
          ['10:58','Invoice paid $40,000','green']].map(([t, e, c], i) => (
          <div key={i} className="flex items-center gap-2 text-[11px]">
            <span className={clsx('h-1.5 w-1.5 rounded-full', c === 'green' ? 'bg-brand-emerald' : 'bg-brand-gold')} />
            <span className="text-zinc-500 font-mono w-10">{t}</span>
            <span className="text-zinc-200 truncate">{e}</span>
          </div>
        ))}
      </Cell>
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2">
      <Cell className="col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Trading activity by tenant</div>
        <div className="space-y-1.5">
          {[['Acme Trading', 92], ['NovaTerra', 74], ['Meridian Desk', 61], ['ArcTrade', 48], ['Cirrus', 33]].map(([n, p]) => (
            <div key={n} className="flex items-center gap-2">
              <div className="text-[11px] text-zinc-200 w-32 truncate">{n}</div>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-brand-gold to-[#FFE17A]" style={{ width: `${p}%` }} />
              </div>
              <div className="text-[10px] font-mono text-zinc-500 w-8 text-right">{p}</div>
            </div>
          ))}
        </div>
      </Cell>
      <Cell className="space-y-2">
        <div className="flex items-center gap-2"><Bell className="h-3.5 w-3.5 text-brand-gold" /><div className="text-[10px] uppercase tracking-widest text-zinc-500">Notifications</div></div>
        <div className="rounded-lg bg-brand-gold/[0.06] border border-brand-gold/20 px-2.5 py-2 text-[11px]"><span className="text-brand-gold font-semibold">Action:</span> 3 auto-doc chapters awaiting review.</div>
        <div className="rounded-lg bg-white/[0.02] px-2.5 py-2 text-[11px] text-zinc-300">System health: nominal · all 3 regions.</div>
      </Cell>
    </div>
  </ViewFrame>
)

// ─── 2. AI Mentor ────────────────────────────────────────────────────────────
export const AIMentorView = () => (
  <ViewFrame
    title="AI Mentor"
    subtitle="Every user receives an intelligent AI assistant trained on your platform knowledge."
    icon={Bot}
    tone="orange"
  >
    <div className="grid grid-cols-12 gap-3 h-full">
      <Cell className="col-span-8 flex flex-col gap-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Conversation</div>
        <div className="self-end max-w-[80%] rounded-2xl rounded-br-md bg-white/[0.04] px-3 py-2 text-[12px] text-zinc-200">What is the current bias on BTC/USDT?</div>
        <div className="self-start max-w-[80%] rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Retrieved knowledge</div>
          {['HTF bias · 4H structure', 'Volume profile', 'Order block — 63.0k'].map((c) => (
            <div key={c} className="flex items-center gap-2 text-[11px] text-zinc-300">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald" />
              <BookOpen className="h-3 w-3 text-brand-gold/70" />{c}
            </div>
          ))}
        </div>
        <div className="self-start max-w-[85%] rounded-2xl rounded-tl-md border border-brand-gold/20 bg-brand-gold/[0.06] px-3 py-2.5 text-[12px] text-zinc-100 leading-relaxed">
          BTC broke the 4H resistance at $63.4k with rising volume. Order block held on the retest. Bias is bullish while price stays above $62.8k.
          <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Confidence <span className="text-brand-gold">74%</span></div>
            <div className="h-1 w-24 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-brand-gold to-[#FFE17A]" style={{ width: '74%' }} /></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {['What invalidates this?', 'Risk to reward for a long?', 'Show me on chart'].map((c) => (
            <span key={c} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.02] px-2.5 py-1 text-[10px] text-zinc-300">
              <ChevronRight className="h-3 w-3 text-brand-gold/70" />{c}
            </span>
          ))}
        </div>
        <div className="mt-auto flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2">
          <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
          <div className="flex-1 text-[11px] text-zinc-500">Ask Kalki Ai Cloud AI anything…</div>
          <div className="h-6 w-6 rounded-md bg-brand-gold/15 flex items-center justify-center text-brand-gold"><Send className="h-3 w-3" /></div>
        </div>
      </Cell>
      <div className="col-span-4 space-y-2 flex flex-col">
        <Cell>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Suggested prompts</div>
          <div className="space-y-1.5">
            {['Give me an overview of the platform',
              'How do I set up a strategy?',
              'Explain drawdown vs max drawdown',
              'Which pairs are trending today?'].map((p) => (
              <div key={p} className="flex items-center gap-2 rounded-lg bg-white/[0.02] border border-white/5 px-2.5 py-1.5 text-[11px] text-zinc-300">
                <MessageSquare className="h-3 w-3 text-brand-gold/70" />{p}
              </div>
            ))}
          </div>
        </Cell>
        <Cell className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Reasoning workflow</div>
          <div className="space-y-1.5 text-[11px]">
            {[['Retrieve', 'green'], ['Ground', 'green'], ['Reason', 'orange'], ['Cite', 'zinc']].map(([s, c]) => (
              <div key={s} className="flex items-center gap-2">
                <span className={clsx('h-4 w-4 rounded-full flex items-center justify-center text-[9px]',
                  c === 'green' ? 'bg-brand-emerald/20 text-brand-emerald' :
                  c === 'orange'? 'bg-brand-gold/20 text-brand-gold animate-pulse' :
                                  'bg-white/[0.05] text-zinc-500')}>{c === 'green' ? '✓' : c === 'orange' ? '▶' : '○'}</span>
                <span className="text-zinc-200">{s}</span>
              </div>
            ))}
          </div>
        </Cell>
      </div>
    </div>
  </ViewFrame>
)

// ─── 3. Trading Terminal ───────────────────────────────────────────────────
export const TradingTerminalView = () => (
  <ViewFrame
    title="Trading Terminal"
    subtitle="Professional-grade trading tools integrated into your platform."
    icon={BarChart3}
  >
    <div className="grid grid-cols-12 gap-2 h-full">
      <Cell className="col-span-8">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] text-zinc-300 font-mono">BTC/USDT <span className="text-brand-emerald ml-2">$63,428.20</span> <span className="text-brand-emerald text-[10px]">+1.82%</span></div>
          <div className="flex items-center gap-1 text-[10px] text-zinc-500">
            {['1m','5m','15m','1H','4H','1D','1W'].map((tf) => (
              <span key={tf} className={clsx('px-1.5 py-0.5 rounded', tf === '4H' ? 'bg-brand-gold/10 text-brand-gold' : '')}>{tf}</span>
            ))}
          </div>
        </div>
        <MiniSparkline data={seededWalk(203, 90, 8, 63000)} stroke="#FFC107" height={260} width={720} className="w-full" />
      </Cell>
      <div className="col-span-4 flex flex-col gap-2">
        <Cell>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Watchlist</div>
          <div className="space-y-1">
            {[['BTC/USDT','63,428','+1.82%','green'],['ETH/USDT','3,412','-0.42%','red'],['SOL/USDT','154.20','+3.10%','green'],['BNB/USDT','594.80','+0.72%','green']].map(([s,p,c,t]) => (
              <div key={s} className="flex items-center justify-between text-[11px]">
                <span className="text-white font-mono">{s}</span>
                <span className="text-zinc-300 font-mono">{p}</span>
                <span className={clsx('font-mono', t === 'green' ? 'text-brand-emerald' : 'text-red-400')}>{c}</span>
              </div>
            ))}
          </div>
        </Cell>
        <Cell>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Order entry</div>
          <div className="grid grid-cols-2 gap-1 text-[11px]">
            {[['Type','LIMIT'],['Side','BUY'],['Size','0.25'],['Price','63,420']].map(([k,v]) => (
              <div key={k} className="rounded bg-white/[0.03] px-2 py-1"><div className="text-[9px] uppercase tracking-widest text-zinc-500">{k}</div><div className="text-white">{v}</div></div>
            ))}
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-1">
            <div className="rounded-lg bg-brand-emerald/15 text-brand-emerald text-[11px] text-center py-1.5 font-semibold">BUY</div>
            <div className="rounded-lg bg-white/[0.03] text-zinc-500 text-[11px] text-center py-1.5">SELL</div>
          </div>
        </Cell>
      </div>
      <Cell className="col-span-8">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Open positions</div>
        <table className="w-full text-[11px]">
          <thead><tr className="text-[9px] uppercase tracking-widest text-zinc-500"><th className="text-left py-1">Symbol</th><th className="text-right">Size</th><th className="text-right">Entry</th><th className="text-right">Mark</th><th className="text-right">P&L</th></tr></thead>
          <tbody>
            {[['BTC-USDT','+0.50','62,180','63,428','+$624'],['ETH-USDT','+4.0','3,320','3,412','+$368'],['SOL-USDT','+40','148.20','154.20','+$240']].map((row) => (
              <tr key={row[0]} className="border-t border-white/5">
                <td className="py-1 text-white font-mono">{row[0]}</td>
                <td className="text-right font-mono text-brand-emerald">{row[1]}</td>
                <td className="text-right text-zinc-400 font-mono">{row[2]}</td>
                <td className="text-right text-zinc-300 font-mono">{row[3]}</td>
                <td className="text-right font-mono text-brand-emerald">{row[4]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Cell>
      <Cell className="col-span-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Risk controls</div>
        <div className="space-y-1 text-[11px]">
          {[['Portfolio heat','1.8%','green'],['Daily P&L','+$1,232','green'],['Max drawdown','2.4%','orange'],['Open risk','$1,800','green']].map(([k,v,t]) => (
            <div key={k} className="flex items-center justify-between">
              <span className="text-zinc-500">{k}</span>
              <span className={clsx('font-mono', t === 'green' ? 'text-brand-emerald' : 'text-brand-gold')}>{v}</span>
            </div>
          ))}
        </div>
      </Cell>
    </div>
  </ViewFrame>
)

// ─── 4. Strategy Builder ───────────────────────────────────────────────────
export const StrategyBuilderView = () => (
  <ViewFrame
    title="Strategy Builder"
    subtitle="Create automated strategies visually without unnecessary complexity."
    icon={Wand2}
    tone="orange"
  >
    <div className="grid grid-cols-12 gap-2 h-full">
      <Cell className="col-span-8">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Strategy canvas · SMC Trend</div>
        <div className="relative h-[280px] rounded-lg border border-white/5 bg-[radial-gradient(circle,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:16px_16px]">
          {[
            { x: 6,   y: 12, kind: 'Trigger',    label: 'BOS on 4H',       tone: 'orange' },
            { x: 30,  y: 12, kind: 'Filter',     label: 'Trend up on 1D',  tone: 'orange' },
            { x: 54,  y: 12, kind: 'Confirm',    label: 'OB + Volume',     tone: 'orange' },
            { x: 78,  y: 12, kind: 'Entry',      label: 'Limit @ OB',      tone: 'green'  },
            { x: 18,  y: 62, kind: 'Risk',       label: 'Size = 1.5%',     tone: 'green'  },
            { x: 42,  y: 62, kind: 'Stop-loss',  label: 'Below OB',        tone: 'green'  },
            { x: 66,  y: 62, kind: 'Exit',       label: '1st TP + BE',     tone: 'green'  },
          ].map((b, i) => (
            <div
              key={i}
              className={clsx(
                'absolute rounded-lg border px-2.5 py-1.5 backdrop-blur',
                b.tone === 'orange' ? 'border-brand-gold/40 bg-brand-gold/10' : 'border-brand-emerald/40 bg-brand-emerald/10',
              )}
              style={{ left: `${b.x}%`, top: `${b.y}%` }}
            >
              <div className={clsx('text-[9px] uppercase tracking-widest', b.tone === 'orange' ? 'text-brand-gold' : 'text-brand-emerald')}>{b.kind}</div>
              <div className="text-[11px] text-white whitespace-nowrap">{b.label}</div>
            </div>
          ))}
          {/* Simple SVG connector */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            {[[15,15,30,15],[41,15,54,15],[66,15,78,15],[85,20,25,60],[25,70,45,70],[45,70,65,70]].map((p, i) => (
              <line key={i} x1={p[0]} y1={p[1]} x2={p[2]} y2={p[3]} stroke="rgba(255,193,7,0.35)" strokeWidth="0.25" strokeDasharray="0.6,0.4" />
            ))}
          </svg>
        </div>
      </Cell>
      <Cell className="col-span-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Indicators</div>
        <div className="grid grid-cols-2 gap-1.5">
          {['EMA-21', 'EMA-50', 'RSI-14', 'ATR', 'Vol Profile', 'Structure'].map((n) => (
            <div key={n} className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5 text-[11px] text-zinc-200">{n}</div>
          ))}
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Validation preview</div>
        <div className="space-y-1 text-[11px]">
          {[['Rules complete','✓','green'],['No cycles','✓','green'],['Backtest ready','✓','green'],['Live capable','✓','green']].map(([k, v, c]) => (
            <div key={k} className="flex items-center justify-between">
              <span className="text-zinc-500">{k}</span>
              <span className={clsx('font-mono', c === 'green' ? 'text-brand-emerald' : 'text-brand-gold')}>{v}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-lg bg-brand-gold/10 border border-brand-gold/25 px-2.5 py-2 text-[11px] text-brand-gold">Backtest · Deploy · Paper mode</div>
      </Cell>
    </div>
  </ViewFrame>
)

// ─── 5. Backtesting ──────────────────────────────────────────────────────────
export const BacktestingView = () => {
  const equity = seededWalk(311, 140, 4, 100)
  const dd = equity.map((v, i) => Math.min(0, v - Math.max(...equity.slice(0, i + 1))))
  return (
    <ViewFrame
      title="Backtesting"
      subtitle="Validate every strategy before deployment using comprehensive historical analysis."
      icon={LineChart}
      tone="green"
    >
      <div className="grid grid-cols-6 gap-2">
        <Kpi label="Return"       value="+38.4%" tone="green" />
        <Kpi label="Win-rate"     value="61.4%"  tone="green" />
        <Kpi label="PF"           value="1.83"   tone="green" />
        <Kpi label="Sharpe"       value="1.42"   tone="green" />
        <Kpi label="Max DD"       value="-6.4%"  tone="red" />
        <Kpi label="Trades"       value="142" />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Cell className="col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Equity curve</div>
          <MiniSparkline data={equity} stroke="#10B981" height={140} width={620} className="w-full" />
        </Cell>
        <Cell>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Drawdown</div>
          <MiniSparkline data={dd} stroke="#ef4444" filled height={100} width={280} className="w-full" />
        </Cell>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Cell>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Monthly performance</div>
          <div className="grid grid-cols-12 gap-1">
            {Array.from({ length: 12 }).map((_, i) => {
              const seed = (i * 9007 + 32) % 233280
              const v = ((seed / 233280) - 0.4) * 8
              const intensity = Math.min(1, Math.abs(v) / 5)
              const bg = v >= 0 ? `rgba(0,200,83,${0.2 + intensity * 0.6})` : `rgba(239,68,68,${0.2 + intensity * 0.6})`
              return <div key={i} className="aspect-square rounded" style={{ backgroundColor: bg }} />
            })}
          </div>
        </Cell>
        <Cell>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Recent trades</div>
          <div className="space-y-0.5 text-[11px] font-mono">
            {[['BTC','LONG','+2.1R','green'],['ETH','LONG','+1.4R','green'],['SOL','LONG','+0.9R','green'],['BNB','SHORT','-0.6R','red'],['XRP','LONG','+1.1R','green']].map(([s,side,r,c], i) => (
              <div key={i} className="flex items-center justify-between border-t border-white/5 py-1">
                <span className="text-zinc-300 w-8">{s}</span>
                <span className={clsx('w-14', side === 'LONG' ? 'text-brand-emerald' : 'text-red-400')}>{side}</span>
                <span className={clsx('text-right', c === 'green' ? 'text-brand-emerald' : 'text-red-400')}>{r}</span>
              </div>
            ))}
          </div>
        </Cell>
      </div>
    </ViewFrame>
  )
}

// ─── 6. Portfolio ─────────────────────────────────────────────────────────────
export const PortfolioView = () => (
  <ViewFrame
    title="Portfolio"
    subtitle="Monitor portfolio performance with institutional-quality analytics."
    icon={Wallet}
    tone="green"
  >
    <div className="grid grid-cols-4 gap-2">
      <Kpi label="NAV"         value="$120,432" delta="+3.4%" tone="green" />
      <Kpi label="Cash"        value="$18,220" />
      <Kpi label="Positions"   value="7 open" />
      <Kpi label="Realised P&L" value="+$4,120" delta="+8.1%" tone="green" />
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2">
      <Cell className="col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Performance trend · 90d</div>
        <MiniSparkline data={seededWalk(407, 90, 4, 100)} stroke="#10B981" height={140} width={620} className="w-full" />
      </Cell>
      <Cell>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Allocation</div>
        <div className="space-y-1.5">
          {[['BTC', 42, 'bg-brand-gold'], ['ETH', 24, 'bg-brand-emerald'], ['SOL', 12, 'bg-purple-400'], ['BNB', 8, 'bg-yellow-400'], ['Cash', 14, 'bg-zinc-500']].map(([n, p, c]) => (
            <div key={n} className="flex items-center gap-2 text-[11px]">
              <span className={clsx('h-2 w-2 rounded', c)} />
              <span className="text-zinc-200 w-10">{n}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className={clsx('h-full', c)} style={{ width: `${p}%` }} /></div>
              <span className="text-zinc-500 font-mono w-8 text-right">{p}%</span>
            </div>
          ))}
        </div>
      </Cell>
    </div>
    <div className="mt-3">
      <Cell>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Holdings</div>
        <table className="w-full text-[11px]">
          <thead><tr className="text-[9px] uppercase tracking-widest text-zinc-500"><th className="text-left py-1">Symbol</th><th className="text-right">Qty</th><th className="text-right">Avg</th><th className="text-right">Mark</th><th className="text-right">Value</th><th className="text-right">Exposure</th><th className="text-right">P&L</th></tr></thead>
          <tbody>
            {[['BTC','0.80','60,320','63,428','$50,742','42%','+$2,486','green'],
              ['ETH','8.50','3,180','3,412','$29,002','24%','+$1,972','green'],
              ['SOL','95','132','154.20','$14,649','12%','+$2,109','green'],
              ['BNB','16','548','594.80','$9,517','8%','+$749','green'],
              ['XRP','5000','0.5240','0.5240','$2,620','2%','—','neutral']].map((row) => (
              <tr key={row[0]} className="border-t border-white/5">
                <td className="py-1 text-white font-mono">{row[0]}</td>
                <td className="text-right text-zinc-300 font-mono">{row[1]}</td>
                <td className="text-right text-zinc-400 font-mono">{row[2]}</td>
                <td className="text-right text-zinc-300 font-mono">{row[3]}</td>
                <td className="text-right text-white font-mono">{row[4]}</td>
                <td className="text-right text-zinc-400 font-mono">{row[5]}</td>
                <td className={clsx('text-right font-mono', row[7] === 'green' ? 'text-brand-emerald' : 'text-zinc-500')}>{row[6]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Cell>
    </div>
  </ViewFrame>
)

// ─── 7. Market Viewer ────────────────────────────────────────────────────
export const MarketViewerView = () => (
  <ViewFrame
    title="Market Viewer"
    subtitle="Understand the market through intelligent visualization and AI-assisted analysis."
    icon={PieChart}
  >
    <div className="grid grid-cols-3 gap-2">
      <Kpi label="Assets tracked" value="142" />
      <Kpi label="Market cap"     value="$2.42T" delta="+1.8%" tone="green" />
      <Kpi label="24h volume"     value="$92.4B" />
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2">
      <Cell className="col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Market heatmap</div>
        <div className="grid grid-cols-8 gap-1">
          {Array.from({ length: 40 }).map((_, i) => {
            const seed = (i * 7919 + 12) % 233280
            const v = ((seed / 233280) - 0.5) * 10
            const intensity = Math.min(1, Math.abs(v) / 5)
            const bg = v >= 0 ? `rgba(0,200,83,${0.15 + intensity * 0.55})` : `rgba(239,68,68,${0.15 + intensity * 0.55})`
            const size = 40 + Math.abs(v) * 3
            return (
              <div key={i} className="aspect-square rounded flex items-center justify-center text-[9px] font-mono text-white/80" style={{ backgroundColor: bg }}>
                {v >= 0 ? '+' : ''}{v.toFixed(1)}
              </div>
            )
          })}
        </div>
      </Cell>
      <Cell>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Trending symbols</div>
        <div className="space-y-1.5">
          {[['BTC/USDT','+1.82%','green'],['SOL/USDT','+3.10%','green'],['DOGE/USDT','+2.60%','green'],['XRP/USDT','-1.10%','red'],['LINK/USDT','+4.20%','green']].map(([s, c, t], i) => (
            <div key={s} className="flex items-center justify-between">
              <span className="text-[11px] text-white font-mono">{s}</span>
              <MiniSparkline data={seededWalk(i + 501, 30, 5, 100)} stroke={t === 'green' ? '#10B981' : '#ef4444'} height={16} width={70} />
              <span className={clsx('text-[10px] font-mono', t === 'green' ? 'text-brand-emerald' : 'text-red-400')}>{c}</span>
            </div>
          ))}
        </div>
      </Cell>
    </div>
    <div className="mt-3">
      <Cell>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">AI market insights</div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <div className="rounded-lg bg-brand-emerald/[0.06] border border-brand-emerald/20 px-3 py-2 text-zinc-100">
            <div className="text-brand-emerald font-semibold mb-0.5">Bullish signal</div>
            BTC trending up with volume expansion on 4H.
          </div>
          <div className="rounded-lg bg-brand-gold/[0.06] border border-brand-gold/20 px-3 py-2 text-zinc-100">
            <div className="text-brand-gold font-semibold mb-0.5">Watch</div>
            SOL breaking prior resistance — confirm on 1D.
          </div>
          <div className="rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2 text-zinc-300">
            <div className="text-zinc-300 font-semibold mb-0.5">Neutral</div>
            XRP compressing under key horizontal.
          </div>
        </div>
      </Cell>
    </div>
  </ViewFrame>
)

// ─── 8. Scanner ────────────────────────────────────────────────────────────────
export const ScannerView = () => (
  <ViewFrame
    title="Scanner"
    subtitle="Discover high-probability opportunities faster with intelligent market scanning."
    icon={ScanLine}
    tone="orange"
  >
    <div className="grid grid-cols-12 gap-2 h-full">
      <Cell className="col-span-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Technical filters</div>
        <div className="space-y-1.5 text-[11px]">
          {[['BOS 4H', true], ['Volume spike', true], ['OB retest', true], ['RSI < 30', false], ['EMA cross', true], ['Divergence', false]].map(([n, on]) => (
            <div key={n} className="flex items-center gap-2">
              <span className={clsx('h-4 w-7 rounded-full relative transition-colors', on ? 'bg-brand-gold/60' : 'bg-white/10')}>
                <span className={clsx('absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform', on ? 'left-3.5' : 'left-0.5')} />
              </span>
              <span className="text-zinc-200">{n}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Saved scans</div>
        <div className="space-y-1 text-[11px]">
          {['SMC Trend Continuation', 'Compression Breakouts', 'Overnight Gap-ups'].map((n) => (
            <div key={n} className="rounded bg-white/[0.02] border border-white/5 px-2 py-1 text-zinc-300">{n}</div>
          ))}
        </div>
      </Cell>
      <div className="col-span-9 space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">AI-generated opportunities</div>
          <div className="ml-auto text-[10px] text-brand-gold font-mono">14 hits</div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[['BTC/USDT','92%','LONG'],['SOL/USDT','88%','LONG'],['LINK/USDT','84%','LONG'],['AVAX/USDT','81%','LONG'],['ARB/USDT','78%','LONG'],['OP/USDT','76%','LONG'],['ETH/USDT','72%','LONG'],['XRP/USDT','68%','SHORT']].map(([s, conf, side], i) => (
            <div key={s} className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5">
              <div className="flex items-center justify-between">
                <div className="text-[11px] text-white font-mono">{s}</div>
                <div className={clsx('text-[10px] font-semibold', side === 'LONG' ? 'text-brand-emerald' : 'text-red-400')}>{side}</div>
              </div>
              <MiniSparkline data={seededWalk(i + 601, 30, 5, 100)} stroke="#FFC107" height={30} width={180} className="mt-1 w-full" />
              <div className="mt-1 flex items-center justify-between text-[10px] text-zinc-500">
                <span>Confluence</span>
                <span className="text-brand-gold font-mono">{conf}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </ViewFrame>
)

// ─── 9. Trading Academy ───────────────────────────────────────────────────
export const TradingAcademyView = () => (
  <ViewFrame
    title="Trading Academy"
    subtitle="Educate traders directly within your own branded ecosystem."
    icon={GraduationCap}
    tone="green"
  >
    <div className="grid grid-cols-12 gap-2 h-full">
      <Cell className="col-span-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-brand-gold/15 text-brand-gold flex items-center justify-center"><PlayCircle className="h-4 w-4" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Current lesson</div>
              <div className="text-white text-sm font-semibold">SMC Foundations · Entry mechanics</div>
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 font-mono">3 / 6</div>
        </div>
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-brand-gold to-[#FFE17A]" style={{ width: '62%' }} /></div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Learning paths</div>
        <div className="grid grid-cols-2 gap-2">
          {[['SMC Foundations','62%','Intermediate','orange'],['Risk & Position Sizing','100%','Beginner','green'],['AI-Assisted Trading','34%','Advanced','orange'],['Options 101','15%','Beginner','orange']].map(([t, p, l, c]) => (
            <div key={t} className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
              <div className="text-white text-[13px] font-semibold mt-0.5">{t}</div>
              <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden"><div className={clsx('h-full', c === 'green' ? 'bg-brand-emerald' : 'bg-gradient-to-r from-brand-gold to-[#FFE17A]')} style={{ width: p }} /></div>
              <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{p}</div>
            </div>
          ))}
        </div>
      </Cell>
      <div className="col-span-4 space-y-2 flex flex-col">
        <Cell>
          <div className="flex items-center gap-2 mb-1.5"><BookOpen className="h-3.5 w-3.5 text-brand-gold" /><div className="text-[10px] uppercase tracking-widest text-zinc-500">Assessment</div></div>
          <div className="text-white text-sm font-semibold">Module 3 quiz</div>
          <div className="text-[11px] text-zinc-400">12 questions · 20 min</div>
        </Cell>
        <Cell>
          <div className="flex items-center gap-2 mb-1.5"><Award className="h-3.5 w-3.5 text-brand-emerald" /><div className="text-[10px] uppercase tracking-widest text-zinc-500">Certificate</div></div>
          <div className="text-white text-sm font-semibold">Risk & Position Sizing</div>
          <div className="text-[11px] text-zinc-400">Awarded 3 days ago · Grade A</div>
        </Cell>
        <Cell className="flex-1">
          <div className="flex items-center gap-2 mb-2"><Users className="h-3.5 w-3.5 text-brand-gold" /><div className="text-[10px] uppercase tracking-widest text-zinc-500">Student progress</div></div>
          <div className="space-y-1 text-[11px]">
            {[['Enrollments','1,842','+94'],['Certificates','214','+18'],['Avg completion','68%','—']].map(([k,v,d]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-zinc-500">{k}</span>
                <span className="text-white font-mono">{v}</span>
                <span className="text-brand-emerald font-mono">{d}</span>
              </div>
            ))}
          </div>
        </Cell>
      </div>
    </div>
  </ViewFrame>
)

// ─── 10. Billing ────────────────────────────────────────────────────────────────
export const BillingView = () => (
  <ViewFrame
    title="Billing"
    subtitle="Manage subscriptions, licensing, and recurring revenue from one interface."
    icon={CreditCard}
  >
    <div className="grid grid-cols-4 gap-2">
      <Kpi label="MRR"           value="$120,000" delta="+8.4%" tone="green" />
      <Kpi label="Active plans"  value="12"        delta="+2"    tone="green" />
      <Kpi label="Churn 30d"     value="1.4%"      delta="-0.2"  tone="green" />
      <Kpi label="Coupons active" value="3" />
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2">
      <Cell className="col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Recent invoices</div>
        <table className="w-full text-[11px]">
          <thead><tr className="text-[9px] uppercase tracking-widest text-zinc-500"><th className="text-left py-1">Invoice</th><th className="text-left">Tenant</th><th className="text-left">Plan</th><th className="text-right">Amount</th><th className="text-right">Status</th></tr></thead>
          <tbody>
            {[['INV-1240','Acme Trading','Half-Year','$40,000','paid'],
              ['INV-1239','Meridian Desk','Quarterly','$25,000','paid'],
              ['INV-1238','ArcTrade','Annual','$60,000','pending'],
              ['INV-1237','NovaTerra','Half-Year','$40,000','paid'],
              ['INV-1236','Cirrus','Quarterly','$25,000','paid']].map(([i,t,pl,a,s]) => (
              <tr key={i} className="border-t border-white/5">
                <td className="py-1.5 text-white font-mono">{i}</td>
                <td className="text-zinc-300">{t}</td>
                <td className="text-zinc-400">{pl}</td>
                <td className="text-right text-white font-mono">{a}</td>
                <td className={clsx('text-right font-mono', s === 'paid' ? 'text-brand-emerald' : 'text-brand-gold')}>{s}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Cell>
      <Cell>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Payment method</div>
        <div className="rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2 flex items-center gap-2">
          <div className="h-6 w-9 rounded bg-white/10 flex items-center justify-center text-[8px] font-mono text-zinc-300">STRIPE</div>
          <div>
            <div className="text-[11px] text-white">Stripe · auto-charge</div>
            <div className="text-[10px] text-zinc-500">visa •••• 4242</div>
          </div>
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Upcoming renewals</div>
        <div className="space-y-1 text-[11px]">
          {[['ArcTrade', 'Nov 12'], ['Cirrus', 'Nov 14'], ['Zeta Prop', 'Nov 22']].map(([n, d]) => (
            <div key={n} className="flex items-center justify-between">
              <span className="text-zinc-300">{n}</span>
              <span className="text-zinc-500 font-mono">{d}</span>
            </div>
          ))}
        </div>
      </Cell>
    </div>
  </ViewFrame>
)

// ─── 11. Analytics ─────────────────────────────────────────────────────────────
export const AnalyticsView = () => (
  <ViewFrame
    title="Analytics"
    subtitle="Turn platform data into business intelligence."
    icon={BarChart3}
    tone="green"
  >
    <div className="grid grid-cols-5 gap-2">
      <Kpi label="User growth"    value="+14.2%" delta="30d"  tone="green" />
      <Kpi label="Engagement"     value="24m"    delta="avg session" />
      <Kpi label="Revenue"        value="$1.2M" delta="+8.4%" tone="green" />
      <Kpi label="AI usage"       value="1.4M"   delta="tokens" />
      <Kpi label="Trading volume" value="$92.4M" delta="7d" />
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2">
      <Cell className="col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Platform engagement · 90d</div>
        <MiniSparkline data={seededWalk(801, 100, 5, 100)} stroke="#FFC107" height={140} width={620} className="w-full" />
      </Cell>
      <Cell>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">AI usage by module</div>
        <div className="space-y-1.5">
          {[['Mentor', 62], ['Scanner', 18], ['Backtest', 12], ['Analytics', 5], ['Other', 3]].map(([n, p]) => (
            <div key={n} className="flex items-center gap-2 text-[11px]">
              <span className="text-zinc-200 w-16">{n}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-brand-gold to-[#FFE17A]" style={{ width: `${p}%` }} /></div>
              <span className="text-zinc-500 font-mono w-8 text-right">{p}%</span>
            </div>
          ))}
        </div>
      </Cell>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-2">
      <Cell>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Trading metrics</div>
        <div className="grid grid-cols-2 gap-1.5 text-[11px]">
          {[['Trades / day','420'],['Win-rate','58.4%'],['Avg P&L / user','+$62'],['Retention 30d','92%']].map(([k,v]) => (
            <div key={k} className="flex items-center justify-between rounded bg-white/[0.02] border border-white/5 px-2 py-1.5">
              <span className="text-zinc-500">{k}</span><span className="text-white font-mono">{v}</span>
            </div>
          ))}
        </div>
      </Cell>
      <Cell>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Revenue by plan</div>
        <div className="space-y-1.5">
          {[['Annual','42%','#FFC107'],['Half-Year','38%','#FFE17A'],['Quarterly','20%','#009640']].map(([n, p, c]) => (
            <div key={n} className="flex items-center gap-2 text-[11px]">
              <span className="h-2 w-2 rounded" style={{ backgroundColor: c }} />
              <span className="text-zinc-200 w-16">{n}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full" style={{ width: p, backgroundColor: c }} /></div>
              <span className="text-zinc-500 font-mono w-8 text-right">{p}</span>
            </div>
          ))}
        </div>
      </Cell>
    </div>
  </ViewFrame>
)

// ─── 12. White-Label Management ───────────────────────────────────────────
export const WhiteLabelView = () => (
  <ViewFrame
    title="White-Label Management"
    subtitle="Launch and manage your own branded AI trading platform effortlessly."
    icon={Building2}
    tone="orange"
  >
    <div className="grid grid-cols-12 gap-2 h-full">
      <Cell className="col-span-5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Brand identity</div>
        <div className="flex items-center gap-3 mb-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-gold to-[#FFE17A] text-black font-bold text-lg flex items-center justify-center">A</div>
          <div>
            <div className="text-white font-semibold">Acme Trading</div>
            <div className="text-[10px] font-mono text-zinc-500">platform.acme.com</div>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Theme tokens</div>
        <div className="grid grid-cols-4 gap-1.5 text-[9px]">
          <div><div className="h-8 rounded bg-brand-gold" /><div className="mt-1 font-mono text-zinc-400">--primary</div><div className="font-mono text-zinc-600">#FFC107</div></div>
          <div><div className="h-8 rounded bg-brand-emerald" /><div className="mt-1 font-mono text-zinc-400">--success</div><div className="font-mono text-zinc-600">#009640</div></div>
          <div><div className="h-8 rounded bg-white" /><div className="mt-1 font-mono text-zinc-400">--text</div><div className="font-mono text-zinc-600">#ffffff</div></div>
          <div><div className="h-8 rounded bg-black border border-white/10" /><div className="mt-1 font-mono text-zinc-400">--bg</div><div className="font-mono text-zinc-600">#000000</div></div>
        </div>
      </Cell>
      <Cell className="col-span-4">
        <div className="flex items-center gap-2 mb-1.5"><Globe className="h-3.5 w-3.5 text-brand-gold" /><div className="text-[10px] uppercase tracking-widest text-zinc-500">Custom domain</div></div>
        <div className="rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2">
          <div className="text-[11px] font-mono text-white">platform.acme.com</div>
          <div className="mt-1 flex items-center gap-2 text-[10px]">
            <span className="inline-flex items-center gap-1 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-1.5 py-0.5 text-brand-emerald"><CheckCircle2 className="h-2.5 w-2.5" />SSL</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-1.5 py-0.5 text-brand-emerald"><CheckCircle2 className="h-2.5 w-2.5" />CDN</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-1.5 py-0.5 text-brand-emerald"><CheckCircle2 className="h-2.5 w-2.5" />DNS</span>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 mb-1.5"><Settings2 className="h-3.5 w-3.5 text-brand-gold" /><div className="text-[10px] uppercase tracking-widest text-zinc-500">Workspace settings</div></div>
        <div className="space-y-1 text-[11px]">
          {[['Modules','8 / 12 enabled'],['SSO','Google + SAML'],['Regions','ap-south, eu-west']].map(([k,v]) => (
            <div key={k} className="flex items-center justify-between rounded bg-white/[0.02] border border-white/5 px-2 py-1">
              <span className="text-zinc-500">{k}</span><span className="text-white font-mono">{v}</span>
            </div>
          ))}
        </div>
      </Cell>
      <Cell className="col-span-3 space-y-2">
        <div className="flex items-center gap-2"><Palette className="h-3.5 w-3.5 text-brand-gold" /><div className="text-[10px] uppercase tracking-widest text-zinc-500">Licence</div></div>
        <div className="rounded-lg bg-brand-gold/[0.08] border border-brand-gold/25 px-3 py-2">
          <div className="text-[10px] uppercase tracking-widest text-brand-gold">Half-Year</div>
          <div className="text-white font-semibold">6 months · $40,000</div>
          <div className="text-[10px] text-zinc-500 font-mono mt-1">Renews Jan 12, 2027</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Preview email</div>
        <div className="rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2 text-[11px] text-zinc-200 leading-relaxed">
          Welcome to <span className="text-brand-gold font-semibold">Acme Trading</span>. Your workspace is ready…
        </div>
      </Cell>
    </div>
  </ViewFrame>
)

// ─── Registry ───────────────────────────────────────────────────────────────
// Exported map so InteractiveProduct.jsx doesn't have to hard-code a
// giant switch — keeps that file's routing table small and readable.
export const VIEW_REGISTRY = {
  'dashboard':      DashboardView,
  'ai-mentor':      AIMentorView,
  'trading':        TradingTerminalView,
  'strategy':       StrategyBuilderView,
  'backtest':       BacktestingView,
  'portfolio':      PortfolioView,
  'market-viewer':  MarketViewerView,
  'scanner':        ScannerView,
  'academy':        TradingAcademyView,
  'billing':        BillingView,
  'analytics':      AnalyticsView,
  'white-label':    WhiteLabelView,
}
